import { useRouter } from 'next/router'

import GetStartedPlansConfig from '@config/get-started-plans-config'

import type { CollectionProducts, GetStartedPlansConfig as GetStartedPlansConfigType } from '@lib/types'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import Logo from '@components/global/Logo'

import LandingProductCarousel from './LandingProductCarousel'

type GetStartedLandingProps = {
	onComplete: () => void
	collectionProducts?: CollectionProducts
	pageContent?: GetStartedPageContent
}

export default function GetStartedLanding({ onComplete, collectionProducts, pageContent: pageContentProp }: GetStartedLandingProps) {
	const router = useRouter()
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const config = GetStartedPlansConfig as GetStartedPlansConfigType
	const landing = config.landingStep

	if (!landing) return null

	return (
		<div className="flex flex-col h-[100dvh] bg-white">
			{/* Fixed top: Logo */}
			<div className="flex-shrink-0 bg-white">
				<div className="container">
					<div className="flex justify-center py-3 md:py-5">
						<button
							type="button"
							aria-label="Birchbox Home"
							className="cursor-pointer"
							onClick={() => {
								if (typeof window !== 'undefined' && window.history.length > 1) {
									router.back()
								} else {
									router.push('/')
								}
							}}
						>
							<Logo className="w-28 md:w-32 fill-bbx-style" />
						</button>
					</div>
				</div>
			</div>

			{/* Scrollable content — inner wrapper centers vertically when content is shorter than viewport */}
			<div className="flex-1 overflow-y-auto bg-white">
				<div className="min-h-full flex flex-col justify-center">
				{/* Hero block: headline + value + subtitle + benefits — unified, no dividers */}
				<div className="px-4 pt-6 pb-10 text-center container max-w-2xl mx-auto">
					<h1 className="text-2xl md:text-4xl font-serif font-medium leading-tight mb-2">
						{landing.heading}
					</h1>
					<p className="text-sm md:text-base font-bold text-bbx-spark uppercase tracking-wider mb-1">
						{pageContent.valueProposition}
					</p>
					<p className="text-sm text-gray-500 mb-6">
						{landing.subtitle} <span aria-label="heart">&#10084;&#65039;</span>
					</p>

					{/* Benefits — inline chips, no background wall */}
					<div className="flex justify-center flex-wrap gap-2 md:gap-3">
						{pageContent.benefits.map(({ icon, label }) => (
							<div
								key={label}
								className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-1.5"
							>
								<i
									className={`${icon} text-sm text-bbx-style`}
									aria-hidden="true"
								/>
								<span className="text-xs md:text-sm font-medium text-gray-700 whitespace-nowrap">
									{label.replace(/\n/g, ' ')}
								</span>
							</div>
						))}
					</div>
				</div>

				{/* Product sneak-peek carousel */}
				{collectionProducts && collectionProducts.length > 0 && (
					<div className="pb-6">
						<p className="text-sm font-bold text-bbx-style text-center mb-4 uppercase tracking-widest px-4">
							{pageContent.carouselHeading.replace('{month}', new Date().toLocaleString('en-US', { month: 'long' }))}
						</p>
						<LandingProductCarousel
							products={collectionProducts}
							moreTitle={pageContent.carouselMoreTitle}
							moreSubtext={pageContent.carouselMoreSubtext}
						/>
					</div>
				)}
				</div>{/* end min-h-full flex centering wrapper */}
			</div>

			{/* Fixed bottom: CTA */}
			<div className="flex-shrink-0 bg-white border-t border-gray-100 pb-[env(safe-area-inset-bottom)]">
				<div className="px-4 py-4 md:px-12 max-w-4xl mx-auto w-full md:flex md:justify-center container">
					<button
						type="button"
						className="button button-action btn-lg animate-cta-attention w-full md:w-auto md:min-w-[200px]"
						onClick={(e) => {
							e.currentTarget.style.pointerEvents = 'none'
							onComplete()
						}}
					>
						{landing.ctaText}
					</button>
				</div>
			</div>
		</div>
	)
}
